import { Injectable, Signal, inject, signal } from "@angular/core";
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from "@angular/router";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

export type BannerSeverity = "info" | "success" | "warning" | "error";
export type BannerLifetime = "route" | "application";

export interface BannerMessage {
  readonly id: string;
  readonly severity: BannerSeverity;
  readonly title: string;
  readonly message: string;
  readonly dismissible: boolean;
  readonly lifetime: BannerLifetime;
  readonly owner: string | null;
}

export interface BannerOptions {
  readonly id?: string;
  readonly title?: string;
  readonly dismissible?: boolean;
  readonly lifetime?: BannerLifetime;
  readonly owner?: string;
}

export interface BannerRequest extends BannerOptions {
  readonly severity?: BannerSeverity;
  readonly message: string;
}

const DEFAULT_TITLES: Record<BannerSeverity, string> = {
  info: "Information",
  success: "Success",
  warning: "Warning",
  error: "Error",
};

/**
 * Application-level messages rendered by the shell below the masthead.
 *
 * `info`, `success`, `warning`, and `error` are the common one-call entry points. A stable `id`
 * updates a message in place instead of stacking duplicates. Route messages are cleared only when
 * navigation successfully reaches a different URL identity; application messages remain until
 * their owner clears them.
 */
@Injectable({
  providedIn: "root",
})
export class BannerService {
  private readonly router = inject(Router, { optional: true });
  private readonly bannerState = signal<readonly BannerMessage[]>([]);
  private generatedId = 0;
  private currentRouteIdentity = this.routeIdentityFromUrl(this.router?.url ?? "/");
  private pendingRouteCleanup: {
    readonly navigationId: number;
    readonly banners: ReadonlyMap<string, BannerMessage>;
  } | null = null;

  /** Current application messages. The shell host can subscribe after a message is published. */
  readonly banners: Signal<readonly BannerMessage[]> = this.bannerState.asReadonly();

  constructor() {
    const routerEvents = this.router?.events;
    if (routerEvents) {
      routerEvents
        .pipe(takeUntilDestroyed())
        .subscribe((event) => {
          if (event instanceof NavigationStart) {
            this.pendingRouteCleanup = {
              navigationId: event.id,
              banners: new Map(
                this.bannerState()
                  .filter((banner) => banner.lifetime === "route")
                  .map((banner) => [banner.id, banner]),
              ),
            };
            return;
          }

          if (event instanceof NavigationEnd) {
            const nextRouteIdentity = this.routeIdentityFromUrl(event.urlAfterRedirects);
            if (nextRouteIdentity !== this.currentRouteIdentity) {
              this.clearRouteBannersFromSnapshot(event.id);
            }
            this.currentRouteIdentity = nextRouteIdentity;
            this.pendingRouteCleanup = null;
            return;
          }

          if (
            (event instanceof NavigationCancel || event instanceof NavigationError)
            && event.id === this.pendingRouteCleanup?.navigationId
          ) {
            this.pendingRouteCleanup = null;
          }
        });
    }
  }

  private clearRouteBannersFromSnapshot(navigationId: number): void {
    const snapshot = this.pendingRouteCleanup;
    if (!snapshot || snapshot.navigationId !== navigationId) {
      return;
    }

    this.bannerState.update((current) =>
      current.filter((candidate) => snapshot.banners.get(candidate.id) !== candidate),
    );
  }

  private routeIdentityFromUrl(url: string): string {
    return url.trim() || "/";
  }

  show(request: BannerRequest): string {
    const message = request.message.trim();
    if (!message) {
      throw new Error("BannerService requires a non-empty message.");
    }

    const severity = request.severity ?? "info";
    const id = this.resolveId(request.id);
    const owner = this.normalizeOptionalValue(request.owner);
    const title = this.normalizeOptionalValue(request.title) ?? DEFAULT_TITLES[severity];
    const banner: BannerMessage = {
      id,
      severity,
      title,
      message,
      dismissible: request.dismissible ?? false,
      lifetime: request.lifetime ?? "route",
      owner,
    };

    this.bannerState.update((current) => {
      const existingIndex = current.findIndex((candidate) => candidate.id === id);
      if (existingIndex === -1) {
        return [...current, banner];
      }

      const next = [...current];
      next[existingIndex] = banner;
      return next;
    });
    return id;
  }

  info(message: string, options: BannerOptions = {}): string {
    return this.show({ ...options, message, severity: "info" });
  }

  success(message: string, options: BannerOptions = {}): string {
    return this.show({ ...options, message, severity: "success" });
  }

  warning(message: string, options: BannerOptions = {}): string {
    return this.show({ ...options, message, severity: "warning" });
  }

  error(message: string, options: BannerOptions = {}): string {
    return this.show({ ...options, message, severity: "error" });
  }

  /** Removes a message only when its caller marked it dismissible. */
  dismiss(id: string): void {
    const banner = this.bannerState().find((candidate) => candidate.id === id);
    if (banner?.dismissible) {
      this.remove(id);
    }
  }

  /** Removes a message regardless of whether it is user-dismissible. */
  remove(id: string): void {
    this.bannerState.update((current) => current.filter((candidate) => candidate.id !== id));
  }

  clearOwner(owner: string): void {
    const normalizedOwner = this.normalizeOptionalValue(owner);
    if (!normalizedOwner) {
      return;
    }
    this.bannerState.update((current) =>
      current.filter((candidate) => candidate.owner !== normalizedOwner),
    );
  }

  clear(): void {
    this.bannerState.set([]);
  }

  private resolveId(requestedId: string | undefined): string {
    if (requestedId === undefined) {
      this.generatedId += 1;
      return `application-banner-${this.generatedId}`;
    }

    const id = requestedId.trim();
    if (!id) {
      throw new Error("BannerService requires a non-empty id when one is supplied.");
    }
    return id;
  }

  private normalizeOptionalValue(value: string | undefined): string | null {
    const normalized = value?.trim();
    return normalized ? normalized : null;
  }

}
